// ============================================================
// components/stashcard/NoteComposer.tsx
//
// The "log to History" composer. A textarea + auto-log channel chip
// (the channel the user last interacted with) + a Send button.
// Writes through to the store's `addNote` action.
// ============================================================

import { useState } from 'react';
import { Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge as _Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useAddNote,
  useReachState,
  useStashCardDetailStore,
} from '../../store/stash-card-detail/hooks';
import { cn } from '@/lib/utils';

const Badge = _Badge as React.ComponentType<{
  variant?: string;
  className?: string;
  children?: React.ReactNode;
}>;

interface NoteComposerProps {
  className?: string;
}

export function NoteComposer({ className }: NoteComposerProps): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const addNote = useAddNote();
  const reach = useReachState();
  const card = useStashCardDetailStore(s => s.card);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || !card) return;
    addNote({
      stashCardId: card.recordID,
      title: '',
      detail: draft.trim(),
      isPrivate: false,
      attachments: [],
    });
    setDraft('');
  };

  const channelLabel = reach?.primary.channel ?? 'note';

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex items-start gap-3 rounded-xl border border-border bg-card p-3.5',
        className,
      )}
    >
      <Avatar className="mt-0.5 h-8 w-8">
        <AvatarImage src={undefined} />
        <AvatarFallback className="bg-[var(--stash-blue)]/10 text-[var(--stash-blue)] text-xs font-bold">
          YO
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <label htmlFor="note-composer-input" className="sr-only">
          What happened on the last call?
        </label>
        <textarea
          id="note-composer-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="What happened on the call?"
          rows={1}
          className="w-full resize-none border-0 bg-transparent text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0 [field-sizing:content]"
        />

        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge
              variant="secondary"
              className="h-5 rounded-full px-2 text-[10px] font-medium uppercase tracking-wider"
            >
              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current align-middle" />
              {channelLabel}
            </Badge>
            <span>· just now</span>
          </div>
          <Button type="submit" size="sm" disabled={!draft.trim()}>
            <Send size={14} />
            <span className="ml-1">Log to History</span>
          </Button>
        </div>
      </div>
    </form>
  );
}
